<script setup lang="ts">
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import type { HeaderJob } from '~/models/Strapi'
import type { JobItem, JobResponse } from '~/models/Job'

interface Props {
  datas: HeaderJob
}

const props = defineProps<Props>()
const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const modalOpen = ref(false)
const isLoading = ref(false)
const jobItems = ref<JobItem[]>()
const selectedTitle = computed(() => route.query?.term ? route.query?.term : 'Marketing')
const selectedLevel = computed(() => route.query?.level ? route.query?.level : 'freshgraduate')
const level = ref([
  { value: 'freshgraduate', label: t('jobreco_page_freshgraduate_title'), description: t('jobreco_page_freshgraduate_desc') },
  { value: 'junior', label: t('jobreco_page_entry_title'), description: t('jobreco_page_entry_desc') },
  { value: 'senior', label: t('jobreco_page_senior_title'), description: t('jobreco_page_senior_desc') },
])
const breakpoints = {
  640: {
    itemsToShow: 2,
  },
}

function changeFilter(newTerm: string | string[], newLevel: string | string[]) {
  router.push({
    path: route.path,
    query: {
      term: newTerm,
      level: newLevel,
    },
  })
  getJobCard(newTerm, newLevel)
}

async function getJobCard(newTerm: string | string[], newLevel: string | string[]) {
  isLoading.value = true
  const { data: items } = await useSearchApi<JobResponse>('/v2/jobs/search', {
    initialCache: false,
    method: 'POST',
    body: {
      country: useGetSubdomain(),
      term: newTerm,
      level: newLevel,
      size: 6,
      type: 'simple_title_with_similar_titles_search',
    },
  })
  jobItems.value = items.value.hits
  isLoading.value = false
}

function toSalaryReport(term: string) {
  const newTerm = term.toLowerCase().replaceAll(' ', '-').replaceAll(',', '-').replaceAll('--', '-')
  return `/salary-report/${newTerm}/everywhere?currency=USD&period=monthly`
}

onMounted(async () => {
  await nextTick()
  getJobCard(selectedTitle.value, selectedLevel.value)
})
</script>

<template>
  <BaseAtomsSkeletonBounded y-padding="lg" class="text-gray-700 !p-0">
    <div class="w-full sm:w-[500px] lg:w-[300px] mx-auto space-y-4 text-center">
      <ContinueFormModal :open="modalOpen" :redirect-val="props.datas?.button_redirection" @open-modal="(item: boolean) => modalOpen = item" />
      <p class="font-bold text-4xl leading-12">
        {{ props.datas?.title }}
      </p>
      <p class="text-sm">
        {{ props.datas?.subtitle }}
      </p>
    </div>
    <div class="grid">
      <BaseAtomsButton v-if="props.datas?.button_label" size="md" classes="font-semibold w-full md:max-w-68 mx-auto my-10" @click="modalOpen = true">
        {{ props.datas?.button_label }}
      </BaseAtomsButton>

      <div class="mx-auto grid grid-cols-2 gap-x-4 gap-y-2 md:flex items-center w-full md:max-w-screen-sm bg-gray-50 p-2">
        <div v-for="(item, idx) in props.datas.tabs" :key="idx" :class="`${selectedTitle === item.label ? 'bg-[#ECE9FC] text-[#5843E5]' : 'text-gray-600'} md:w-48 text-center font-bold rounded-md py-2.5 cursor-pointer`" @click="changeFilter(item.label, selectedLevel)">
          {{ item.label }}
        </div>
        <div class="text-center text-gray-600 md:w-48 font-bold rounded-md py-2.5 cursor-pointer" @click="modalOpen = true">
          {{ props.datas.button_other_label }}
        </div>
      </div>
      <Menu v-slot="{ open }" as="div" class="mx-auto my-10">
        <MenuButton class="!py-2 !px-4 focus:outline-none font-medium rounded flex items-center">
          <div class="flex items-center">
            {{ $t('global_for') }}
            <span class="font-bold text-sm ml-1 mr-2">{{ level.find(item => item.value === selectedLevel)?.label }}</span>
            <Icon name="heroicons:chevron-down" :class="open ? 'rotate-180 transform' : ''" class="h-4 w-4" />
          </div>
        </MenuButton>
        <transition
          enter-active-class="transition duration-100 ease-out" enter-from-class="transform scale-95 opacity-0"
          enter-to-class="transform scale-100 opacity-100" leave-active-class="transition duration-75 ease-in"
          leave-from-class="transform scale-100 opacity-100" leave-to-class="transform scale-95 opacity-0"
        >
          <MenuItems
            class="absolute shadow-xl w-64 origin-top-right rounded-md border-0 ring-0 focus:outline-none z-10 bg-white p-3 space-y-4 cursor-pointer"
          >
            <MenuItem v-for="(item, idx) in level" :key="idx" @click="changeFilter(selectedTitle, item.value)">
              <div>
                <p class="font-bold mb-1">
                  {{ item.label }}
                </p>
                <p class="text-xs text-gray-400 leading-4">
                  {{ item.description }}
                </p>
              </div>
            </MenuItem>
          </MenuItems>
        </transition>
      </Menu>
    </div>
    <div v-if="isLoading" class="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-y-10 gap-x-6">
      <div v-for="(i) in 6" :key="i" :class="`${i > 2 ? 'hidden lg:block' : (i === 2 ? 'hidden sm:block' : '')} w-full`">
        <div class="w-full flex items-center mb-2">
          <div class="w-[35%] md:w-[20%] bg-gray-200 h-17 rounded-lg mr-2" />
          <div class="w-full space-y-1">
            <div class="w-[45%] md:w-[30%] bg-gray-200 h-5 rounded-md" />
            <div class="w-[65%] md:w-[60%] bg-gray-200 h-5 rounded-md" />
            <div class="w-[100%] md:w-[100%] bg-gray-200 h-5 rounded-md" />
          </div>
        </div>
        <div class="w-[35%] bg-gray-200 h-8 mb-1 rounded-md" />
        <div class="w-[100%] bg-gray-200 h-10 rounded-md" />
      </div>
    </div>
    <div v-else-if="isEmpty(jobItems) && !isLoading" class="text-center space-y-6">
      <img class="mx-auto w-full max-w-240px" src="/images/no-result.svg" alt="Workflow">
      <div>
        <p class="text-2xl font-bold">
          {{ $t('global_something_went_wrong') }}...
        </p>
        <p class="mt-3">
          {{ $t('jobreco_page_try_other_job') }}
        </p>
      </div>
      <div class="flex my-4">
        <div class="mx-auto flex items-center space-x-4">
          <span v-for="(item, idx) in props.datas.tabs.filter((item: any) => item.label !== selectedTitle)" :key="idx" class="text-indigo-500 cursor-pointer" @click="changeFilter(item.label, selectedLevel)">
            {{ item.label }}
          </span>
        </div>
      </div>
      <p>{{ $t('jobreco_page_checkout_salary') }}</p>
      <div class="flex my-4">
        <div class="mx-auto flex items-center space-x-4">
          <a v-for="(item, idx) in props.datas.tabs" :key="idx" :href="toSalaryReport(item.label)" target="_blank" class="flex items-center text-indigo-500 cursor-pointer">
            {{ item.label }} {{ $t('global_salary') }}
            <Icon name="material-symbols:open-in-new" class="w-4 h-4 ml-1" />
          </a>
        </div>
      </div>
    </div>
    <div v-else>
      <Carousel class="block lg:hidden py-3" :wrap-around="true" :items-to-scroll="3" :breakpoints="breakpoints" snap-align="start">
        <Slide v-for="(item, idx) in jobItems" :key="idx" class="p-1 !block">
          <BrowsejobsJobCard :item="item" :force-click-navigate="true" :job-id="item.job_id" impression-source="featured" />
        </Slide>
      </Carousel>
      <div class="hidden lg:grid lg:grid-cols-3 xl:grid-cols-3 gap-y-10 gap-x-6">
        <BrowsejobsJobCard v-for="(jobItem, idx) in jobItems" :key="idx" impression-source="featured" :item="jobItem" :job-id="jobItem.job_id" />
      </div>
    </div>
  </BaseAtomsSkeletonBounded>
</template>
